import React, { useEffect, useMemo, useRef } from 'react';
import { DRAG_THRESHOLD, swallowNextClick } from './events';
import { flowToScreen } from './geometry';
import type { Side } from './geometry';
import type { CanvasStoreBundle, Connecting } from './canvasStore';
import type { ViewportApi } from './useViewport';
import type { PlanEdge } from '../types';

/** The drop magnet, in screen pixels. */
const MAGNET = 20;

interface Options {
  edges: PlanEdge[];
  isProposed: (id: string) => boolean;
  onConnect: (sourceId: string, targetId: string) => void;
}

export interface ConnectApi {
  onHandlePointerDown: (e: React.PointerEvent, nodeId: string, side: Side) => void;
}

/** A left handle is the target side: dragging from it makes the dropped-on node the source. */
const orient = (originId: string, fromSide: Side, otherId: string) =>
  fromSide === 'right' ? { source: originId, target: otherId } : { source: otherId, target: originId };

export function useConnect(bundle: CanvasStoreBundle, viewport: ViewportApi, opts: Options): ConnectApi {
  const { store } = bundle;
  const latest = useRef(opts);
  latest.current = opts;
  const cleanup = useRef<(() => void) | null>(null);

  useEffect(() => () => cleanup.current?.(), []);

  const isValidConnection = (source: string, target: string) => {
    const { edges, isProposed } = latest.current;
    if (source === target) return false;
    if (isProposed(source) || isProposed(target)) return false;
    // Duplicates are refused one way only: A to B and B to A may both exist.
    return !edges.some((e) => e.source_id === source && e.target_id === target);
  };

  const findTarget = (clientX: number, clientY: number, originId: string, fromSide: Side): Connecting['target'] => {
    // Trap 9, drop by distance alone: the element under the pointer is the source
    // of truth, and the nearest handle within 20px is only a magnet behind it.
    const hit = (typeof document.elementFromPoint === 'function' ? document.elementFromPoint(clientX, clientY) : null) as HTMLElement | null;
    const handle = hit?.closest?.('[data-handle-side]');
    const handleNode = handle?.closest('[data-node-id]')?.getAttribute('data-node-id');
    if (handle && handleNode && handleNode !== originId) {
      return { nodeId: handleNode, side: handle.getAttribute('data-handle-side') as Side };
    }

    const p = viewport.panePoint(clientX, clientY);
    const vp = viewport.vp.current;
    const { nodes } = store.getState();
    let best: Connecting['target'] = null;
    let bestDist = MAGNET;
    Object.keys(nodes).forEach((id) => {
      const b = nodes[id];
      if (id === originId || !b.measured || latest.current.isProposed(id)) return;
      (['left', 'right'] as Side[]).forEach((side) => {
        const c = flowToScreen({ x: side === 'right' ? b.x + b.width : b.x, y: b.y + (b.handleY ?? b.height / 2) }, vp);
        const d = Math.hypot(c.x - p.x, c.y - p.y);
        if (d < bestDist) {
          bestDist = d;
          best = { nodeId: id, side };
        }
      });
    });
    if (best) return best;

    // Over a card but away from its handles: the card still takes the arrow.
    const card = hit?.closest?.('[data-node-id]')?.getAttribute('data-node-id');
    if (card && card !== originId) return { nodeId: card, side: fromSide === 'right' ? 'left' : 'right' };
    return null;
  };

  const onHandlePointerDown = (e: React.PointerEvent, nodeId: string, side: Side) => {
    // Drawing arrows is a desktop task; a finger on a handle pans like anywhere else.
    if (e.pointerType === 'touch' || e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    cleanup.current?.();
    const pointerId = e.pointerId;
    const start = { x: e.clientX, y: e.clientY };
    let active = false;

    const finish = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('keydown', key, true);
      cleanup.current = null;
    };

    const move = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      if (!active) {
        if (Math.hypot(ev.clientX - start.x, ev.clientY - start.y) < DRAG_THRESHOLD) return;
        active = true;
      }
      const target = findTarget(ev.clientX, ev.clientY, nodeId, side);
      const pair = target ? orient(nodeId, side, target.nodeId) : null;
      store.setState({
        connecting: {
          sourceId: nodeId,
          fromSide: side,
          pointer: viewport.clientToFlow(ev.clientX, ev.clientY),
          target,
          valid: pair ? isValidConnection(pair.source, pair.target) : null,
        },
      });
    };

    const up = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      finish();
      const c = store.getState().connecting;
      store.setState({ connecting: null });
      if (!active) return;
      swallowNextClick();
      if (c?.target && c.valid) {
        const pair = orient(nodeId, side, c.target.nodeId);
        latest.current.onConnect(pair.source, pair.target);
      }
    };

    const cancel = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      finish();
      store.setState({ connecting: null });
    };

    const key = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return;
      ev.stopPropagation();
      finish();
      store.setState({ connecting: null });
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('keydown', key, true);
    cleanup.current = finish;
  };

  // Reads the store and refs only; one handler serves every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => ({ onHandlePointerDown }), [bundle, viewport]);
}
